import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging, MessagePayload } from 'firebase/messaging';
import { getAnalytics, Analytics } from 'firebase/analytics';
import api from '@/lib/api';
import { getCookie } from 'cookies-next';
import { toast } from 'react-toastify';
import React, { createElement } from 'react';

// Firebase configuration interface
export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

// Cache for Firebase config
let cachedConfig: FirebaseConfig | null = null;
let configPromise: Promise<FirebaseConfig> | null = null;

// Get Firebase configuration from API route
export const getFirebaseConfig = async (): Promise<FirebaseConfig> => {
  // Return cached config if available
  if (cachedConfig) {
    return cachedConfig;
  }

  // Return existing promise if config is being fetched
  if (configPromise) {
    return configPromise;
  }

  // Fetch config from API route
  configPromise = fetch('/api/firebase-config')
    .then((response) => {
      if (!response.ok) {
        throw new Error('Failed to fetch Firebase configuration');
      }
      return response.json();
    })
    .then((config: FirebaseConfig) => {
      cachedConfig = config;
      return config;
    })
    .catch((error) => {
      console.error('Error fetching Firebase config:', error);
      // Return empty config as fallback
      return {
        apiKey: '',
        authDomain: '',
        projectId: '',
        storageBucket: '',
        messagingSenderId: '',
        appId: '',
      };
    })
    .finally(() => {
      configPromise = null;
    });

  return configPromise;
};

// Synchronous version for backward compatibility (returns cached config or empty)
const getFirebaseConfigSync = (): FirebaseConfig => {
  if (cachedConfig) {
    return cachedConfig;
  }
  // Return empty config if not yet loaded
  return {
    apiKey: '',
    authDomain: '',
    projectId: '',
    storageBucket: '',
    messagingSenderId: '',
    appId: '',
  };
};

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;
let analytics: Analytics | null = null;

// Initialize Firebase
export const initializeFirebase = async (): Promise<FirebaseApp | null> => {
  if (typeof window === 'undefined') {
    // Server-side: return null
    return null;
  }

  // Return existing app if already initialized
  const existingApps = getApps();
  if (existingApps.length > 0) {
    app = existingApps[0];
    return app;
  }

  const config = await getFirebaseConfig();

  // Check if config is valid
  if (!config.apiKey || !config.projectId) {
    console.warn('Firebase configuration is incomplete. Push notifications will not work.');
    return null;
  }

  try {
    app = initializeApp(config);
    return app;
  } catch (error) {
    console.error('Firebase initialization error:', error);
    return null;
  }
};

// Get Firebase Messaging instance
export const getFirebaseMessaging = async (): Promise<Messaging | null> => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!app) {
    app = await initializeFirebase();
  }

  if (!app) {
    return null;
  }

  if (messaging) {
    return messaging;
  }

  try {
    // Check if browser supports messaging
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = 'PushManager' in window;
    
    if (!hasServiceWorker) {
      console.warn('[Firebase] Service Worker not available in navigator');
    }
    if (!hasPushManager) {
      console.warn('[Firebase] PushManager not available in window');
    }

    if (hasServiceWorker && hasPushManager) {
      try {
        messaging = getMessaging(app);
        console.log('[Firebase] ✅ Messaging initialized successfully');
        console.log('[Firebase] Messaging instance:', messaging);
        return messaging;
      } catch (messagingError) {
        console.error('[Firebase] ❌ Error creating messaging instance:', messagingError);
        throw messagingError;
      }
    } else {
      const userAgent = typeof navigator !== 'undefined' && 'userAgent' in navigator ? navigator.userAgent : 'N/A';
      console.warn('[Firebase] Browser does not support push notifications', {
        hasServiceWorker,
        hasPushManager,
        userAgent: userAgent,
        protocol: window.location.protocol,
      });
      return null;
    }
  } catch (error) {
    console.error('[Firebase] ❌ Messaging initialization error:', error);
    console.error('[Firebase] Error details:', {
      error,
      app: !!app,
      hasWindow: typeof window !== 'undefined',
    });
    return null;
  }
};

// Get Firebase Analytics instance
export const getFirebaseAnalytics = async (): Promise<Analytics | null> => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!app) {
    app = await initializeFirebase();
  }

  if (!app) {
    return null;
  }

  if (analytics) {
    return analytics;
  }

  try {
    analytics = getAnalytics(app);
    return analytics;
  } catch (error) {
    console.error('Firebase Analytics initialization error:', error);
    return null;
  }
};

// Request notification permission
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (typeof window === 'undefined') {
    console.warn('[Firebase] Cannot request permission: window is undefined');
    return 'denied';
  }

  if (!('Notification' in window)) {
    console.warn('[Firebase] Notification API not available in this browser');
    return 'denied';
  }

  // Return existing permission if already granted or denied
  if (Notification.permission === 'granted') {
    console.log('[Firebase] Notification permission already granted');
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    console.warn('[Firebase] Notification permission already denied');
    return 'denied';
  }

  // Request permission (only works if permission is 'default')
  try {
    console.log('[Firebase] Requesting notification permission from user...');
    const permission = await Notification.requestPermission();
    console.log('[Firebase] Notification permission result:', permission);
    return permission;
  } catch (error) {
    console.error('[Firebase] Error requesting notification permission:', error);
    return 'denied';
  }
};

// Update Firebase token on backend
const updateFirebaseTokenOnBackend = async (fcmToken: string): Promise<void> => {
  try {
    const companyUserId = getCookie('company_user_id');
    const companyId = getCookie('company_id');

    if (!companyUserId || !companyId) {
      console.warn('[Firebase] Cannot update Firebase token: Missing user authentication data');
      return;
    }

    console.log('[Firebase] Updating Firebase token on backend...');

    // Call the API to update Firebase token
    await api.put('/v1/ca_user_profile/update_firebase_token', {
      p_logged_in_company_user_id: companyUserId,
      p_company_id: companyId,
      p_firebase_token: fcmToken,
    });

    console.log('[Firebase] ✅ Firebase token updated successfully on backend');
  } catch (error) {
    console.error('[Firebase] ❌ Failed to update Firebase token on backend:', error);
    // Don't throw - token generation was successful, backend update failure shouldn't block it
  }
};

// Get FCM token
export const getFCMToken = async (): Promise<string | null> => {
  const messagingInstance = await getFirebaseMessaging();
  
  if (!messagingInstance) {
    console.warn('[Firebase] Messaging is not available');
    return null;
  }

  try {
    const permission = await requestNotificationPermission();
    
    if (permission !== 'granted') {
      console.warn('[Firebase] Notification permission not granted');
      return null;
    }

    console.log('[Firebase] Requesting FCM token...');

    // Request token without VAPID key
    const token = await getToken(messagingInstance, {});

    if (token) {
      console.log('[Firebase] ✅ FCM registration token obtained successfully');
      
      // Update Firebase token on the backend
      await updateFirebaseTokenOnBackend(token);
      
      // Log that push notifications are enabled
      try {
        const { logPushNotificationEnabled } = await import('./notificationLogger');
        await logPushNotificationEnabled(token);
      } catch (logError) {
        console.error('[Firebase] Failed to log push notification enabled:', logError);
      }
      
      return token;
    } else {
      console.warn('[Firebase] No registration token available');
      return null;
    }
  } catch (error) {
    console.error('[Firebase] ❌ Error getting FCM token:', error);
    
    // Provide helpful error messages
    if (error instanceof Error) {
      const errorMessage = error.message || String(error);
      console.error('[Firebase] Error details:', {
        message: errorMessage,
        name: error.name,
      });

      console.error('[Firebase] This might be a Firebase configuration issue');
      console.error('[Firebase] Make sure all required Firebase config values are set correctly');
    } else {
      console.error('[Firebase] Unknown error type:', error);
    }
    
    // Don't throw - return null to prevent unhandled errors
    return null;
  }
};

// Listen for foreground messages
export const onForegroundMessage = async (
  callback: (payload: MessagePayload) => void
): Promise<(() => void) | null> => {
  console.log('[Firebase] Setting up foreground message listener...');
  
  // Ensure Firebase app is initialized first
  if (!app) {
    console.log('[Firebase] Firebase app not initialized, initializing now...');
    await initializeFirebase();
  }
  
  const messagingInstance = await getFirebaseMessaging();
  
  if (!messagingInstance) {
    console.error('[Firebase] ❌ Cannot setup foreground listener: Messaging instance not available');
    console.error('[Firebase] Debug info:', {
      hasWindow: typeof window !== 'undefined',
      hasApp: !!app,
      hasMessaging: !!messaging,
      hasServiceWorker: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
      hasPushManager: typeof window !== 'undefined' && 'PushManager' in window,
    });
    return null;
  }

  console.log('[Firebase] ✅ Messaging instance available, setting up onMessage listener...');
  console.log('[Firebase] Messaging instance:', messagingInstance);

  try {
    const unsubscribe = onMessage(messagingInstance, async (payload) => {
      console.log('[Firebase] 📬 FOREGROUND message received:', payload);
      console.log('[Firebase] Payload structure:', {
        hasNotification: !!payload.notification,
        hasData: !!payload.data,
        notification: payload.notification,
        data: payload.data,
        messageId: payload.messageId,
        from: (payload as any).from,
      });
      
      // Verify message format
      if (!payload.notification && !payload.data) {
        console.error('[Firebase] ⚠️ Message has no notification or data payload');
        console.error('[Firebase] Full payload:', JSON.stringify(payload, null, 2));
      }

      const title = payload.notification?.title || payload.data?.title || 'New Notification';
      const body = payload.notification?.body || payload.data?.body || '';
      const messageId = payload.messageId || 'unknown';
      const icon = payload.notification?.icon || payload.data?.icon;

      // CHECK FIRST: If background handler (onBackgroundMessage) already processed this notification,
      // skip displaying toast in foreground handler to prevent duplicates
      if (typeof window !== 'undefined') {
        // Initialize sets if needed
        if (!(window as any).__displayedNotificationIds) {
          (window as any).__displayedNotificationIds = new Set<string>();
        }
        
        // Check if a toast with this messageId already exists in the DOM (background handler already displayed it)
        const toastId = `push-${messageId}`;
        const existingToast = document.querySelector(`[id*="${toastId}"]`) || 
                             document.querySelector(`[data-toast-id="${toastId}"]`);
        
        // Check if this notification was already displayed by background handler (atomic check)
        const alreadyDisplayedByBackground = (window as any).__displayedNotificationIds.has(messageId) || !!existingToast;
        
        if (alreadyDisplayedByBackground) {
          console.log('[Firebase] ⚠️ Notification already processed by background handler (onBackgroundMessage), skipping foreground toast:', messageId, {
            inSet: (window as any).__displayedNotificationIds.has(messageId),
            inDOM: !!existingToast,
          });
          // Still log the notification (but delay it to avoid dismissing toast)
          setTimeout(async () => {
            try {
              const { logNotification } = await import('./notificationLogger');
              await logNotification({
                title,
                body,
                messageId,
                data: payload.data,
                receivedAt: new Date(),
                notificationType: 'foreground',
              });
            } catch (logError) {
              console.error('[Firebase] Failed to log foreground notification:', logError);
            }
          }, 500); // Delay logging to ensure toast is displayed first
          return; // Exit early - background handler already handled the toast
        }
      }

      // If we reach here, background handler hasn't processed it yet, so foreground handler should display toast
      // Track push notification toasts IMMEDIATELY - BEFORE any API calls to prevent dismissal
      // This must happen FIRST, before any async operations that might trigger API calls
      if (typeof window !== 'undefined') {
        if (!(window as any).__pushNotificationToastIds) {
          (window as any).__pushNotificationToastIds = new Set<string>();
        }
        // Track that a push notification toast will be active (use messageId as identifier)
        (window as any).__pushNotificationToastIds.add(messageId);
        console.log('[Firebase] Tracked push notification toast (foreground, IMMEDIATE):', messageId);
        
        // Mark this notification as displayed IMMEDIATELY (before any async operations)
        if (!(window as any).__displayedNotificationIds) {
          (window as any).__displayedNotificationIds = new Set<string>();
        }
        (window as any).__displayedNotificationIds.add(messageId);
        console.log('[Firebase] Marked notification as displayed (foreground):', messageId);
        
        // Clean up old IDs after 1 hour to prevent memory leaks
        setTimeout(() => {
          if ((window as any).__displayedNotificationIds) {
            (window as any).__displayedNotificationIds.delete(messageId);
          }
        }, 60 * 60 * 1000); // 1 hour
      }

      // Display toast notification for FOREGROUND messages
      // Use requestAnimationFrame to ensure DOM is ready, then setTimeout for ToastContainer
      if (typeof window !== 'undefined') {
        // Try immediate display first (no delays)
        try {
          const notificationUrl = payload.data?.url || payload.data?.click_action || payload.fcmOptions?.link || '/';
          
          // Verify tracking
          if (!(window as any).__pushNotificationToastIds) {
            (window as any).__pushNotificationToastIds = new Set<string>();
          }
          if (!(window as any).__pushNotificationToastIds.has(messageId)) {
            (window as any).__pushNotificationToastIds.add(messageId);
          }
          
          // Check if toast is available
          if (typeof toast === 'undefined' || !toast.info) {
            console.error('[Firebase] ❌ Toast is not available. Make sure ToastContainer is rendered in the app.');
            if ((window as any).__pushNotificationToastIds) {
              (window as any).__pushNotificationToastIds.delete(messageId);
            }
            return;
          }
          
          // Double-check before displaying - toast might have been created by another handler
          const checkToastId = `push-${messageId}`;
          const existingToastCheck = document.querySelector(`[id*="${checkToastId}"]`) || 
                                    document.querySelector(`[data-toast-id="${checkToastId}"]`);
          
          if (existingToastCheck) {
            console.log('[Firebase] ⚠️ Toast already exists in DOM, skipping display:', messageId);
            // Clean up tracking since toast already exists
            if ((window as any).__pushNotificationToastIds) {
              (window as any).__pushNotificationToastIds.delete(messageId);
            }
            return;
          }
          
          // Verify it's still in the displayed set (race condition protection)
          if (!(window as any).__displayedNotificationIds?.has(messageId)) {
            console.log('[Firebase] ⚠️ Notification no longer in displayed set, skipping:', messageId);
            if ((window as any).__pushNotificationToastIds) {
              (window as any).__pushNotificationToastIds.delete(messageId);
            }
            return;
          }
          
          console.log('[Firebase] 🍞 Displaying toast IMMEDIATELY (no delay)...', {
            title,
            body,
            messageId,
          });
          
          // Check if toast with this ID already exists - if so, dismiss it first to override
          const existingToastElement = document.querySelector(`[id*="${checkToastId}"]`) || 
                                      document.querySelector(`[data-toast-id="${checkToastId}"]`);
          if (existingToastElement) {
            console.log('[Firebase] Toast already exists, dismissing to override:', checkToastId);
            toast.dismiss(checkToastId);
          }
          
          // Display toast immediately - no requestAnimationFrame or setTimeout
          const toastMessage = `${title}${body ? ': ' + body : ''}`;
          const toastId = toast.info(toastMessage, {
                position: 'top-right' as const,
                autoClose: false,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                toastId: checkToastId, // Use messageId as toastId - react-toastify will update existing toast
                onClose: () => {
                  console.log('[Firebase] Toast closed (foreground):', messageId);
                  // Clean up tracking when toast is closed
                  // BUT: Keep it in __displayedNotificationIds to prevent re-display (it will auto-remove after 1 hour)
                  if ((window as any).__pushNotificationToastIds) {
                    (window as any).__pushNotificationToastIds.delete(messageId);
                  }
                  // Don't remove from __displayedNotificationIds here - let the timeout handle it
                  // This prevents the same notification from being displayed again if another handler fires
                },
                onClick: () => {
                  console.log('[Firebase] Toast clicked (foreground), navigating to:', notificationUrl);
                  window.location.href = notificationUrl;
                },
          });
          
          console.log('[Firebase] ✅ Toast.info called, returned:', toastId);
          
          // Verify toast appears
          setTimeout(() => {
            const toasts = document.querySelectorAll('.Toastify__toast');
            const container = document.querySelector('.Toastify__toast-container');
            console.log('[Firebase] Toast verification:', {
              toastCount: toasts.length,
              hasContainer: !!container,
              toastId,
              found: Array.from(toasts).some((el: any) => 
                el.id === toastId || el.id?.includes(toastId) || el.getAttribute('data-toast-id') === toastId
              ),
            });
            
            if (toasts.length === 0 && container) {
              console.warn('[Firebase] ⚠️ Toast not found, trying delayed fallback...');
              setTimeout(() => {
                toast.info(toastMessage, {
                  position: 'top-right' as const,
                  autoClose: false,
                  toastId: `push-${messageId}-fallback-${Date.now()}`,
                });
              }, 300);
            }
          }, 100);
        } catch (toastError) {
          console.error('[Firebase] ❌ Error displaying toast:', toastError);
          // Fallback: try with delay
          setTimeout(() => {
            try {
              const toastMessage = `${title}${body ? ': ' + body : ''}`;
              toast.info(toastMessage, {
                position: 'top-right' as const,
                autoClose: false,
                toastId: `push-${messageId}-error-fallback-${Date.now()}`,
              });
            } catch (fallbackError) {
              console.error('[Firebase] ❌ Fallback toast also failed:', fallbackError);
            }
          }, 200);
          
          if ((window as any).__pushNotificationToastIds) {
            (window as any).__pushNotificationToastIds.delete(messageId);
          }
        }
      }

      // Log the notification AFTER a delay to ensure toast is displayed first
      // This prevents the API interceptor from dismissing the toast
      setTimeout(async () => {
        try {
          const { logNotification } = await import('./notificationLogger');
          await logNotification({
            title,
            body,
            messageId,
            data: payload.data,
            receivedAt: new Date(),
            notificationType: 'foreground',
          });
        } catch (logError) {
          console.error('[Firebase] Failed to log foreground notification:', logError);
        }
      }, 500); // Delay logging to ensure toast is displayed first

      // Display browser notification if permission is granted
      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
        try {
          const notificationOptions: NotificationOptions = {
            body: body,
            icon: payload.notification?.icon || payload.data?.icon || '/icons/notification-icon.png',
            badge: '/icons/badge-icon.png',
            tag: payload.data?.tag || messageId,
            data: {
              ...payload.data,
              url: payload.data?.url || payload.fcmOptions?.link || '/',
              click_action: payload.data?.click_action || payload.fcmOptions?.link || '/',
            },
            requireInteraction: typeof payload.data?.requireInteraction === 'boolean' 
              ? payload.data.requireInteraction 
              : String(payload.data?.requireInteraction) === 'true',
          };

          // Show notification
          const notification = new Notification(title, notificationOptions);
          
          // Store URL for click handler
          const notificationUrl = payload.data?.url || payload.data?.click_action || payload.fcmOptions?.link || '/';
          
          // Handle notification click
          notification.onclick = (event) => {
            event.preventDefault();
            
            // Log click
            import('./notificationLogger').then(({ logNotificationClick }) => {
              logNotificationClick({
                title,
                url: notificationUrl,
                messageId,
              }).catch(err => console.error('[Firebase] Failed to log notification click:', err));
            });

            // Navigate to URL
            window.focus();
            window.location.href = notificationUrl;
            
            notification.close();
          };

          // Auto-close after 5 seconds if not clicked
          setTimeout(() => {
            notification.close();
          }, 5000);

          console.log('[Firebase] ✅ Browser notification displayed for foreground message');
        } catch (notificationError) {
          console.error('[Firebase] ❌ Failed to display browser notification:', notificationError);
        }
      } else {
        console.log('[Firebase] Cannot display browser notification: Permission not granted or Notification API not available');
      }
      
      // Call the user's callback
      try {
        callback(payload);
      } catch (callbackError) {
        console.error('[Firebase] Error in foreground message callback:', callbackError);
      }
    });
    
    console.log('[Firebase] ✅ Foreground message listener set up successfully');
    return unsubscribe;
  } catch (error) {
    console.error('[Firebase] ❌ Error setting up foreground message listener:', error);
    return null;
  }
};

export default {
  initializeFirebase,
  getFirebaseMessaging,
  getFirebaseAnalytics,
  requestNotificationPermission,
  getFCMToken,
  onForegroundMessage,
};

